export default {
  id: "shifts",
  title: "Shifts: Clocking, Editing & Approval",
  section: "Daily Operations",
  order: 10,
  keywords: "shift clock in out approve missed edit reason breaks documentation notes admin clock-out geofence mileage error",
  body: `
## What it is

A shift is the unit everything else runs on: scheduling, clocking,
documentation, billing, and payroll all hang off it. The Shifts page lists
every shift with filters for date range, status, payer, caregiver, and
client; the Calendar shows the same shifts by time. Day-to-day admin work —
approving, fixing clock times, completing documentation — happens from
either.

## Shift statuses

- **Scheduled** — created, not started.
- **Clocked In / Clocked Out** — in progress / finished.
- **Missed** — the scheduled end passed with no clock-in.
- **Error** — needs attention: the caregiver never clocked out (the system
  auto-closes it overnight without guessing times), or the nightly check
  flagged it as exceeding the client's authorization.
- **Approved** — reviewed by an admin. Only approved shifts reach billing
  and payroll.
- **Canceled** — canceling always requires a reason.

## How clocking in works

Caregivers clock in from the top bar or their dashboard (the app nudges
them toward the mobile app, but the website works). The Clock In dialog
shows the shift details and, depending on the shift:

- **Service code** — picked automatically, or chosen if the shift carries
  several.
- **Location** — every clock-in and clock-out records the device's location
  by default. Each service code's behavior is configurable in Settings →
  Service Codes → Location Tracking: **Capture only** (record, no check —
  the default), **Enforce geofence** (compare against the client's address;
  within a quarter mile they're set, farther away or with poor GPS accuracy
  they must check an acknowledgment that their location is being logged,
  visible to you afterward — the default for LS, SLN, SLH, and CMP), or
  **Off** (record nothing).
- **Compliance** — expired caregiver compliance can warn or hard-block
  clocking in, and some clients require acknowledging their support
  strategy first.
- **Mileage** — optional starting odometer, starting location, and a
  "transporting client(s)" checkbox. Entering a starting odometer commits
  them to closing the trip at clock-out.

## How clocking out works

Clock-out is where documentation happens, and the dialog shows exactly what
that shift's payer and service code require:

- **VR codes** — the "Activity Completed" pickers (up to the code's limit),
  plus any configured VR questions.
- **Job Development (CE/SE/SJBT/TWE)** — either the code's configured
  questions or the guided Activity / Outcome / Next Steps fields.
- **Job Coaching (JC)** — primary and secondary activity selections (up to
  3 each).
- **DSPD 1-on-1** — the code's configured questions, the client's goals,
  medication administration (each med marked Given with a time, or Skipped
  with a reason), and the concerns question.
- **Training shifts** — training subject, summary, and a drawn signature
  (this books the caregiver's training hours and generates their
  certificate automatically).
- **Everything else** — free-text shift notes, minimum 20 characters.
- **Mileage closure** — if a starting odometer was recorded, ending
  odometer, places visited, and ending location are required (total miles
  computes on screen).

Shadow-role caregivers clocking their own time see none of this — their
clock-out just closes their time row; documentation belongs to the primary.

**DSPD group shifts** clock out through their task list instead: each
assigned Pick Up, Drop Off, Shift Notes, and Medications task must be
completed (or the client marked Did Not Attend) before that caregiver can
clock out — the error names any tasks still open. Pick-up and drop-off
confirmations stamp the current time, but the caregiver can tap **Adjust
time** afterward to record when it really happened (every adjustment is
kept in the shift's history), and admins can correct the times later in
Edit Clock Out Time.

## Step by step: fixing common situations

**Caregiver forgot to clock out (Error shift)**
1. Find the shift (filter Status to Error).
2. Open **Edit Clock Out Time**, set the real end time, enter your change
   reason. The shift becomes Clocked Out and is approvable.

**Caregiver couldn't clock out and called it in**
1. Open the shift's actions and use **Admin Clock Out**.
2. Enter the end time and whatever documentation you have.

**Shift never happened as scheduled but the work was done (Missed)**
1. Open **Approve Missed Shift** on the shift.
2. Enter the **actual start and end times** (total hours compute on
   screen), pick the service code if there are several, and complete the
   same documentation a clock-out would require.
3. Approve — it books exactly like a normal approval.

## Editing rules

- **Admins must enter a change reason on every shift edit** — it goes to
  the audit history, so make it meaningful.
- **Approved shifts lock their billing fields.** Documentation (notes,
  answers) stays editable, but payer and service code don't — undo the
  approval first if billing details are wrong.
- Shifts that would exceed the client's remaining authorization show a
  warning you must acknowledge before saving or approving.

## How approval works

Open **Approve Shift** on a clocked-out shift. The dialog is a review
screen: scheduled vs. actual times, break time and total billable time,
locations with maps, mileage summary, and the full documentation — which
you can still edit here. Approval is blocked until required documentation
exists (same rules as clock-out).

Approving:

- books the billing hours (quarter-hour rounding for VR/DSPD),
- deducts the authorization hours/units,
- books training hours for Staff Training shifts,
- **approves the shift's recorded miles** — the mileage section has an
  "Approve mileage" checkbox (pre-checked) with the miles editable, so you
  can correct or hold them; only approved miles reach payroll. An allowance
  meter under the toggle shows how much of the caregiver's per-period
  mileage cap this approval uses and warns (without blocking) on overruns
  (see the Payroll & Mileage guide),
- and locks the billing fields.

**Undo approval** reopens the shift if something was wrong (training hours
already earned are kept — the time was worked).

## Multiple caregivers on one shift

A shift carries one **primary** caregiver plus optional **shadow** or
**training** caregivers. Side caregivers clock independently and are paid
(payroll includes their hours, even on shifts whose main status is
Missed), but billing, forms, and client documentation always follow the
primary. The Shifts list shows side-row times and a SHADOW WORKED flag.
`,
};
